/**
 * Express Adapter
 *
 * Express.js middleware for UI Bridge server.
 */

import type { Router, Request, Response, NextFunction } from 'express';
import type {
  UIBridgeServerConfig,
  UIBridgeServerHandlers,
  APIResponse,
  RouteDefinition,
  CORSOptions,
} from './types';
import { UI_BRIDGE_ROUTES } from './types';
import { mapInternalErrorCode } from './error-mapper';

/**
 * Express-specific configuration
 */
export interface ExpressAdapterConfig extends UIBridgeServerConfig {
  /** Use JSON body parser (if not already configured) */
  useBodyParser?: boolean;
}

/**
 * Create CORS middleware
 */
function createCORSMiddleware(options: CORSOptions | boolean) {
  const corsOptions: CORSOptions =
    typeof options === 'boolean'
      ? { origin: options, methods: ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'OPTIONS'] }
      : options;

  return (req: Request, res: Response, next: NextFunction) => {
    // Origin
    if (corsOptions.origin === true) {
      res.setHeader('Access-Control-Allow-Origin', '*');
    } else if (typeof corsOptions.origin === 'string') {
      res.setHeader('Access-Control-Allow-Origin', corsOptions.origin);
    } else if (Array.isArray(corsOptions.origin)) {
      const origin = req.headers.origin;
      if (origin && corsOptions.origin.includes(origin)) {
        res.setHeader('Access-Control-Allow-Origin', origin);
      }
    }

    // Methods
    if (corsOptions.methods) {
      res.setHeader('Access-Control-Allow-Methods', corsOptions.methods.join(', '));
    }

    // Headers
    if (corsOptions.headers) {
      res.setHeader('Access-Control-Allow-Headers', corsOptions.headers.join(', '));
    } else {
      res.setHeader(
        'Access-Control-Allow-Headers',
        'Content-Type, Authorization, X-Requested-With'
      );
    }

    // Credentials
    if (corsOptions.credentials) {
      res.setHeader('Access-Control-Allow-Credentials', 'true');
    }

    // Max age
    if (corsOptions.maxAge) {
      res.setHeader('Access-Control-Max-Age', String(corsOptions.maxAge));
    }

    // Handle preflight
    if (req.method === 'OPTIONS') {
      res.status(204).end();
      return;
    }

    next();
  };
}

/**
 * Wrap error in API format
 */
function wrapError(error: Error | string, code?: string): APIResponse<never> {
  const message = typeof error === 'string' ? error : error.message;
  return {
    success: false,
    error: message,
    code: mapInternalErrorCode(code, message),
    timestamp: Date.now(),
  };
}

/**
 * Create Express router with UI Bridge routes
 */
export function createExpressRouter(
  handlers: UIBridgeServerHandlers,
  config: ExpressAdapterConfig = {}
): Router {
  // Dynamic import to avoid bundling Express if not used

  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const express = require('express');
  const router: Router = express.Router();

  // Add CORS middleware if configured
  if (config.cors) {
    router.use(createCORSMiddleware(config.cors));
  }

  // Add body parser if requested.
  //
  // Item B (iter-4 manual-test remediation): `express.json()` (body-parser
  // strict mode in newer versions) returns HTTP 400 "Unexpected end of JSON
  // input" / "EOF" when a POST arrives with `Content-Type: application/json`
  // but a zero-byte body. Routes like `/control/page-health` take no
  // parameters, so callers naturally send `curl -X POST -H 'Content-Type:
  // application/json' .../page-health` without a payload. Normalize an
  // empty body to `{}` BEFORE the JSON parser sees it so the request reaches
  // the handler instead of dying at the middleware layer.
  if (config.useBodyParser) {
    router.use((req: Request, _res: Response, next: NextFunction) => {
      const contentLength = req.headers['content-length'];
      const isEmpty = contentLength === '0' || contentLength === undefined;
      const isJson = (req.headers['content-type'] || '').includes('application/json');
      if (req.method === 'POST' && isEmpty && isJson) {
        // Mark body as already-parsed so express.json() short-circuits.
        // Cast through `unknown` because Request typings consider req.body
        // an unknown value, not a tagged shape.
        (req as unknown as { body: unknown; _body: boolean })._body = true;
        (req as unknown as { body: unknown }).body = {};
      }
      next();
    });
    router.use(express.json());
    // Catch any residual body-parser errors (malformed JSON with non-zero
    // content-length): downgrade to a structured 400 response carrying the
    // canonical `BODY_PARSE_ERROR` code rather than express's default HTML.
    router.use((err: Error & { type?: string }, _req: Request, res: Response, next: NextFunction) => {
      if (err && err.type === 'entity.parse.failed') {
        res.status(400).json(wrapError(err.message || 'Invalid JSON body', 'BODY_PARSE_ERROR'));
        return;
      }
      next(err);
    });
  }

  // Add authentication middleware if configured
  if (config.authenticate) {
    router.use(async (req: Request, res: Response, next: NextFunction) => {
      try {
        const authenticated = await config.authenticate!(req);
        if (!authenticated) {
          res.status(401).json(wrapError('Unauthorized', 'UNAUTHORIZED'));
          return;
        }
        next();
      } catch (error) {
        res.status(500).json(wrapError(error as Error, 'AUTH_ERROR'));
      }
    });
  }

  // P2.2 — Endpoint discovery. Returns every (method, path) pair sorted by
  // path so SDKs and tests can introspect what the server exposes without
  // grepping the source. Mirrors the `/ui-bridge/_routes` endpoint on the
  // runner.
  router.get('/_routes', (_req: Request, res: Response) => {
    const routes = UI_BRIDGE_ROUTES.map((r) => ({ method: r.method, path: r.path })).sort(
      (a, b) => a.path.localeCompare(b.path) || a.method.localeCompare(b.method)
    );
    res.json({
      success: true,
      data: { routes, count: routes.length },
      timestamp: Date.now(),
    });
  });

  // Register routes
  for (const route of UI_BRIDGE_ROUTES) {
    const method = route.method.toLowerCase() as 'get' | 'post' | 'put' | 'delete' | 'patch';
    const path = route.path;
    const handlerName = route.handler as keyof UIBridgeServerHandlers;
    const handler = handlers[handlerName];

    if (!handler) {
      console.warn(`Handler not found for route: ${route.method} ${route.path}`);
      continue;
    }

    router[method](
      path,
      createRouteHandler(route, handler as (...args: unknown[]) => Promise<APIResponse<unknown>>)
    );
  }

  return router;
}

/**
 * Create a route handler from a route definition
 */
function createRouteHandler(
  route: RouteDefinition,
  handler: (...args: unknown[]) => Promise<APIResponse<unknown>>
) {
  return async (req: Request, res: Response) => {
    const start = performance.now();
    try {
      // Extract params
      const args: unknown[] = [];

      if (route.params) {
        for (const param of route.params) {
          args.push(req.params[param]);
        }
      }

      // Add body if required. Defensive normalization (iter-4): an
      // upstream proxy or a body-parser bypass can leave `req.body`
      // undefined even for a POST. Handlers expect an object (e.g.
      // `pageHealth()` takes no args but the route is POST). Coerce to
      // `{}` so destructuring + key-access inside handlers doesn't crash.
      if (route.bodyRequired || route.method === 'POST') {
        args.push(req.body ?? {});
      }

      // Add query params for GET requests
      if (route.method === 'GET' && Object.keys(req.query).length > 0) {
        args.push(req.query);
      }

      const result = await handler(...args);
      const durationMs = Math.round(performance.now() - start);
      res.setHeader('X-Response-Time', `${durationMs}ms`);
      res.json({ ...result, durationMs });
    } catch (error) {
      const durationMs = Math.round(performance.now() - start);
      res.setHeader('X-Response-Time', `${durationMs}ms`);
      res.status(500).json({ ...wrapError(error as Error, 'INTERNAL_ERROR'), durationMs });
    }
  };
}

/**
 * Create Express app with UI Bridge routes
 *
 * Convenience function that creates a complete Express app with UI Bridge.
 */
export function createExpressApp(
  handlers: UIBridgeServerHandlers,
  config: ExpressAdapterConfig = {}
): unknown {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const express = require('express');
  const app = express();

  app.use(express.json());

  const basePath = config.basePath || '/ui-bridge';
  const router = createExpressRouter(handlers, { ...config, useBodyParser: false });

  app.use(basePath, router);

  // Health check
  app.get('/health', (_req: Request, res: Response) => {
    res.json({ status: 'ok', timestamp: Date.now() });
  });

  return app;
}

/**
 * Express middleware that adds UI Bridge to an existing app
 */
export function uiBridgeMiddleware(
  handlers: UIBridgeServerHandlers,
  config: ExpressAdapterConfig = {}
): Router {
  return createExpressRouter(handlers, config);
}
